import { useQuery } from '@tanstack/react-query';
import React from 'react';
import PageMeta from '../../components/common/PageMeta';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import {
  BlockSkeleton,
  Button,
  Callout,
  IconArrowRight,
  IconExternal,
  IconLink,
  ListPage,
  StatusLabel,
} from '../../ui/v2';
import {
  fetchReservationsIntegrationStatus,
  reservationsStatusHasActiveConnection,
} from '../../utils/reservationsIntegrationStatus';
import {
  OpenTablePath,
  openGuestCenter,
  useOrdersIntegration,
} from './integrationParts';
import { reservationPartnerLabel } from './integrationsModel';

export default function IntegrationsPage() {
  const orders = useOrdersIntegration();
  const { data: reservationsStatus, isPending: loadingReservations } = useQuery(
    {
      queryKey: useScopedQueryKey('reservations-integration-status'),
      queryFn: fetchReservationsIntegrationStatus,
    },
  );

  const {
    posInfo,
    connected: isConnected,
    connecting: connectingPos,
    error: posError,
  } = orders;
  const handleConnectPos = orders.connect;
  const reservationsConnected = reservationsStatusHasActiveConnection(
    reservationsStatus ?? null,
  );
  const reservationsConnectedLabel = `Connected to ${reservationPartnerLabel(
    reservationsStatus?.current_reservation_partner,
  )}`;
  const loading = orders.loading || loadingReservations;

  return (
    <>
      <PageMeta
        title="Integrations | Ask Arthur"
        description="Manage your integrations"
      />
      <ListPage title="Integrations">
        <div className="px-4 pb-8 pt-5 lg:px-6">
          {loading ? (
            <div
              role="status"
              className="grid max-w-[960px] grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))] gap-4"
            >
              <span className="sr-only">Loading integrations…</span>
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="rounded-v2-lg border border-v2-line p-5"
                >
                  <BlockSkeleton rows={4} />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid max-w-[960px] grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))] gap-4">
              <IntegrationCard
                title="Reservations"
                heading="Reservation integrations"
                status={
                  reservationsConnected ? (
                    <StatusLabel tone="success">
                      {reservationsConnectedLabel}
                    </StatusLabel>
                  ) : (
                    <StatusLabel tone="neutral" hollow>
                      Setup required
                    </StatusLabel>
                  )
                }
              >
                <LogoPair
                  partner={
                    <img
                      src="/images/logo/opentable-logo.svg"
                      alt="OpenTable"
                      className="h-full w-full object-contain"
                    />
                  }
                />
                {!reservationsConnected && (
                  <div className="mt-auto flex flex-col items-start gap-3 pt-5">
                    <OpenTablePath />
                    <Button
                      variant="primary"
                      onClick={openGuestCenter}
                      trailing={<IconExternal size={12} />}
                    >
                      Open Guest Center
                    </Button>
                  </div>
                )}
              </IntegrationCard>

              <IntegrationCard
                title="Orders"
                heading="Order integrations"
                status={
                  isConnected && posInfo ? (
                    <StatusLabel tone="success">
                      Connected to {posInfo.label}
                    </StatusLabel>
                  ) : posInfo ? (
                    <StatusLabel tone="accent" hollow>
                      {posInfo.label} ready
                    </StatusLabel>
                  ) : (
                    <StatusLabel tone="neutral" hollow>
                      Not configured
                    </StatusLabel>
                  )
                }
              >
                {posInfo ? (
                  <>
                    <LogoPair
                      partner={
                        posInfo.logo ? (
                          <img
                            src={posInfo.logo}
                            alt={posInfo.label}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <span
                            className="text-v2-16 font-bold"
                            style={{ color: posInfo.color }}
                          >
                            POS
                          </span>
                        )
                      }
                    />
                    <p className="mt-5 max-w-[68ch] text-v2-13 text-v2-fg-2">
                      Connect your{' '}
                      <strong className="font-semibold text-v2-fg">
                        {posInfo.label}
                      </strong>{' '}
                      account so Arthur can receive and process phone orders
                      directly into your POS.
                    </p>
                    {!isConnected && (
                      <div className="mt-auto flex flex-col items-start gap-3 pt-5">
                        {posError && (
                          <Callout tone="danger" className="w-full">
                            {posError}
                          </Callout>
                        )}
                        <Button
                          variant="primary"
                          onClick={handleConnectPos}
                          loading={connectingPos}
                          trailing={
                            connectingPos ? undefined : (
                              <IconArrowRight size={14} />
                            )
                          }
                        >
                          {connectingPos
                            ? 'Connecting…'
                            : `Connect ${posInfo.label}`}
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-v2 border border-v2-line text-v2-fg-3">
                        <IconLink size={16} />
                      </div>
                      <div>
                        <p className="text-v2-13 font-medium text-v2-fg">
                          Orders integration not configured
                        </p>
                        <p className="mt-0.5 max-w-[52ch] text-v2-12 text-v2-fg-3">
                          Your account has not been configured to integrate
                          orders. Reach out to support to begin an integration.
                        </p>
                      </div>
                    </div>
                    <div className="mt-auto pt-5">
                      <Button
                        variant="primary"
                        onClick={() =>
                          (window.location.href = 'mailto:support@askarthur.io')
                        }
                        trailing={<IconExternal size={12} />}
                      >
                        Contact support
                      </Button>
                    </div>
                  </>
                )}
              </IntegrationCard>
            </div>
          )}
        </div>
      </ListPage>
    </>
  );
}

const IntegrationCard: React.FC<{
  title: string;
  heading: string;
  status: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, heading, status, children }) => (
  <section
    aria-label={title}
    className="flex min-h-[340px] flex-col rounded-v2-lg border border-v2-line bg-v2-surface p-5"
  >
    <div className="mb-5 flex items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-v2-12 font-medium text-v2-fg-3">{title}</p>
        <h2 className="mt-0.5 text-v2-16 font-semibold text-v2-fg">
          {heading}
        </h2>
      </div>
      <div className="shrink-0 pt-0.5">{status}</div>
    </div>
    <div className="flex flex-1 flex-col">{children}</div>
  </section>
);

const LogoTile: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-v2-lg border border-v2-line bg-white p-2.5">
    {children}
  </div>
);

const LogoPair: React.FC<{ partner: React.ReactNode }> = ({ partner }) => (
  <div className="flex items-center justify-center gap-5 rounded-v2-lg border border-v2-line bg-v2-sunken/60 px-5 py-7">
    <LogoTile>
      <img
        src="/images/logo/askarthur-icon.svg"
        alt="AskArthur"
        className="h-full w-full object-contain"
      />
    </LogoTile>
    <div aria-hidden className="flex items-center gap-1.5 text-v2-fg-4">
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      <span className="h-1.5 w-8 rounded-full bg-current opacity-70" />
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
    </div>
    <LogoTile>{partner}</LogoTile>
  </div>
);
